import React, { useCallback, useState, useMemo } from 'react';
import { ErrorMessage, Form } from '@koopajs/mui';
import { useResourceCreate, useComponentVisibility, useResourceList } from '@koopajs/react';
import { Typography, Button, Box } from '@mui/material';
import { useLocale } from '@koopajs/react';
import { TrackEvent } from '@koopajs/app';
import { TextField } from 'components/temp/TextFieldTemp';
import { TextMultiLine } from 'components/temp/TextMultiLineTemp';
import { useAppSnackbar } from 'components/hooks/useAppSnackbar';
import { IMeeting } from 'types';
import ResourcePicker from 'components/ResourcePicker';
import { getCommitteeMembersTitlesObj } from 'utils/getCommitteeMembersTitlesObj';
import { useCommitteeName } from 'components/hooks/useCommitteeName';
import { IResourcePickerOption } from 'components/ResourcePicker/types';
import { IUserPublicProfile } from '@koopajs/app';
import { DialogFormTemp } from 'components/temp/DialogFormTemp';
import { MinutesPreviewDialog } from './MinutesPreviewDialog';
import { InfoOutlined } from '@mui/icons-material';
import { getResourcePickerOptionsForMeeting } from 'utils/getResourcePickerOptionsForMeeting';
import { getEmailReminderOptions } from 'utils/getEmailReminderOptions';

export const SendMinutes: React.FC = () => {
  const { t } = useLocale();
  const { triggerSnackbar } = useAppSnackbar();

  const keyPrefix = 'Dialogs.DialogSendMinutes';

  const sendMinutesDialog = useComponentVisibility('sendMinutes');

  const [isMinutesPreviewDialogOpen, setMinutesPreviewDialogOpen] = useState(false);

  const { minutes } = sendMinutesDialog.getContext() as {
    minutes: IMeeting;
  };

  const { createResource, errorMessage } = useResourceCreate({
    path: `/meetings/${minutes?.id}/send-minutes`,
    customReducer: {
      path: {
        resourceType: `/meetings`,
        resourceId: minutes?.id
      },
      mapping: (prevObj: unknown, newObj: unknown) => {
        const updatedMeeting = newObj as IMeeting;

        return updatedMeeting;
      }
    }
  });

  const { resources: users } = useResourceList<IUserPublicProfile>({
    path: '/users',
    searchParams: { size: 50 }
  });

  const handleSubmit = useCallback(
    async (data: object): Promise<boolean> => {
      const formData = data as { message?: string; reminder?: number; subject?: string };

      if (!formData.message) {
        formData.message = undefined;
      }

      const res = await createResource(formData);

      if (res) {
        triggerSnackbar({
          snackbarText: t(keyPrefix + '.snackbarSendMinutesSuccess'),
          variant: 'success'
        });

        TrackEvent('minutes-sent');
      } else {
        triggerSnackbar({
          snackbarText: t(keyPrefix + '.snackbarSendMinutesFail'),
          variant: 'error'
        });
      }

      return res;
    },
    [createResource]
  );

  const committeeName = useCommitteeName(minutes?.committeeId);

  const committeeMembersTitlesObj: Record<string, string> = getCommitteeMembersTitlesObj(
    minutes?.activeCommitteeMembers
  );

  const { getUsersOptions, getMeetingReviewersOptions, getDefaultParticipants, getDefaultMeetingReviewers } =
    getResourcePickerOptionsForMeeting();

  const meetingReviewersIds = minutes?.activeCommitteeMembers
    ?.filter((aCM) => aCM.roles?.find((role) => role.role === 'reviewMeetings'))
    .map((aCM) => aCM.userId);

  let emailRecipientsOptions: IResourcePickerOption[] = [];

  // resource picker available options
  if (!minutes?.minutesPublishedAt) {
    //meeting reviewers
    emailRecipientsOptions = getMeetingReviewersOptions(
      users,
      meetingReviewersIds || [],
      committeeMembersTitlesObj
    );
  } else {
    // all users
    emailRecipientsOptions = getUsersOptions(users, committeeMembersTitlesObj);
  }

  // resource picker - default values
  const defaultParticipants = useMemo(() => {
    if (!minutes?.minutesPublishedAt) {
      // meeting reviewers
      return getDefaultMeetingReviewers(meetingReviewersIds || [], users);
    } else {
      // meeting participants
      return getDefaultParticipants(minutes?.participants || [], users);
    }
  }, [
    JSON.stringify(minutes?.minutesPublishedAt),
    JSON.stringify(meetingReviewersIds),
    JSON.stringify(users),
    JSON.stringify(minutes?.participants)
  ]);

  const meetingReminderOptions = getEmailReminderOptions(keyPrefix);

  const handleShowMinutesPreviewDialog = (): void => {
    setMinutesPreviewDialogOpen(true);
  };
  const handleCloseMinutesPreviewDialog = (): void => {
    setMinutesPreviewDialogOpen(false);
  };
  if (!minutes) {
    return null;
  }

  return (
    <DialogFormTemp
      dialogKey="sendMinutes"
      onSubmit={handleSubmit}
      i18n={{
        keyPrefix: `${keyPrefix}.DialogFormSend`,
        options: { committeeName: committeeName, minutesTitle: minutes?.title }
      }}
      maxWidth="md"
      leftAction={<Button onClick={sendMinutesDialog.setHidden}>{t('common:labelCancel')}</Button>}
      dialogProps={{ className: 'rr-block' }}
      secondaryAction={
        <Button onClick={handleShowMinutesPreviewDialog}>{t(keyPrefix + '.previewMinutesLabel')}</Button>
      }
    >
      <MinutesPreviewDialog
        isOpen={isMinutesPreviewDialogOpen}
        onClose={handleCloseMinutesPreviewDialog}
        meetingId={minutes?.id}
      />
      <ErrorMessage error={errorMessage} />

      {!minutes?.minutesPublishedAt && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: '14px' }} role="alert">
          <InfoOutlined sx={{ color: 'customGrey.darker' }} />{' '}
          <Typography variant="body2">{t(keyPrefix + '.alertDocumentInPreparation')}</Typography>
        </Box>
      )}

      <Typography sx={{ my: '14px' }}>{t(keyPrefix + '.Form.sendTo')}</Typography>

      <Box sx={{ mt: 1.5, mb: 2 }}>
        <ResourcePicker
          i18n={{ keyPrefix: keyPrefix + '.Form.FieldEmailRecipients' }}
          name="emailRecipients"
          defaultValue={defaultParticipants}
          resources={emailRecipientsOptions as IResourcePickerOption[]}
          isOpenOnFocus
          isDisabledCloseOnSelect
          isMultiple
          translatedLabel=""
          translatedLabelMultipleSelected=""
          translatedErrorText=""
          isAvatar
          sx={{ mt: 1 }}
        />
      </Box>
      <TextField
        name="subject"
        validationRules={{ maxLength: 256 }}
        i18n={{ keyPrefix: keyPrefix + '.Form.FieldSubject' }}
      />
      <TextMultiLine
        rows={4}
        name="message"
        validationRules={{ maxLength: 1000 }}
        isOptional={true}
        i18n={{ keyPrefix: keyPrefix + '.Form.FieldMessage' }}
      />

      <Form.Radio
        name="reminder"
        i18n={{ keyPrefix: keyPrefix + '.Form.RadioFields' }}
        options={meetingReminderOptions}
        row
        sx={{ mt: 1, pt: 1 }}
        variant="dense"
        isOptional={true}
      />
    </DialogFormTemp>
  );
};
